import React, { FunctionComponent, SyntheticEvent, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Container, Grid, Header } from 'semantic-ui-react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import withStyles from 'react-jss';
import classnames from 'classnames';
import { format } from 'date-fns-tz';
import { isAfter, parseISO } from 'date-fns';

import { ClassProp } from 'helpers/types';
import { Profile } from 'components/UserMgmt/UserMgmt.helpers';
import FormDateInput from 'components/FormComponents/FormDateInput';
import FormTimeInput from 'components/FormComponents/FormTimeInput';
import {
  handleError,
  handleSuccess,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.helpers';
import {
  buildTimeStamp,
  cstToUTC,
  dateIsAfterCurrentDate,
  DATE_FORMAT,
  getCurrentCSTDateWithTime,
  oneMinuteToMidnight,
  validateDates,
} from 'helpers/date';
import { Segment } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';

import { GET_PROFILE } from 'components/UserMgmt/UserMgmt.queries';
import { GET_OFFER } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/scenes/PulseOffer/PulseOffer.queries';

import MODIFY_SEGMENT_PROPERTIES from './setCouponSegmentProperties.mutations';

import styles from './ModifySegment.styles';
import FormInput from 'components/FormComponents/FormInput';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  segment: Segment;
  offerUuid: string;
  cancelUpdate: () => void;
};

const defaultSuccessMessage = [
  {
    displayMessage: 'Success!',
    message: 'Segment termination scheduled successfully.',
  },
];
interface ModifySegmentFormData {
  segmentUuid: string;
  endDate: Date;
  endTime: string;
  startDate: Date;
  startTime: string;
  updatedBy?: string;
  segmentPriority: string;
}
interface UserProfileData {
  userProfile: Profile;
}

const currentDate = new Date();

const ModifySegment: FunctionComponent<Props> = ({
  segment,
  cancelUpdate,
  classes,
  offerUuid,
}) => {
  const { uuid, endDate, startDate, segmentPriority } = segment;
  const methods = useForm<ModifySegmentFormData>({
    mode: 'onBlur',
    defaultValues: {
      startDate: parseISO(startDate),
      startTime: format(parseISO(startDate), 'HH:mm'),
      endTime: format(oneMinuteToMidnight(), 'HH:mm'),
      segmentUuid: uuid,
      segmentPriority: segmentPriority.toString(),
    },
  });

  const { data, loading: profileLoading } = useQuery<UserProfileData>(
    GET_PROFILE,
  );

  const checkDates = () => {
    const startDate = getValues().startDate;
    const endDate = getValues().endDate;

    if (startDate && endDate) {
      return validateDates(startDate, endDate);
    }
    return true;
  };

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState,
    trigger,
    watch,
  } = methods;

  const startTime = watch('startTime');
  const endTime = watch('endTime');

  useEffect(() => {
    if (profileLoading || !data) return;
    const {
      userProfile: { givenName, familyName },
    } = data;
    setValue('updatedBy', `${givenName} ${familyName}`);
  }, [data, profileLoading]);

  useEffect(() => {
    register(
      { name: 'startDate' },
      {
        validate: {
          oneOf: () => checkDates() || 'Start Date must come before End Date',
          isAfter: (value) =>
            dateIsAfterCurrentDate(
              getCurrentCSTDateWithTime(value, getValues().startTime),
            ) || "Start Date can't be earlier than today",
        },
      },
    );
    register({ name: 'startTime' });
    register(
      { name: 'endDate' },
      {
        required: 'End Date is required',
        validate: {
          oneOf: () => checkDates() || 'End Date must come after Start Date',
          isAfter: (value) =>
            dateIsAfterCurrentDate(
              getCurrentCSTDateWithTime(value, getValues().endTime),
            ) || "End Date can't be earlier than today",
        },
      },
    );
    register({ name: 'endTime' });
  }, [register]);

  useEffect(() => {
    trigger('startDate');
  }, [startTime]);

  useEffect(() => {
    trigger('endDate');
  }, [endTime]);

  const handleGetOfferResponse = () => {
    handleSuccess(defaultSuccessMessage);
    cancelUpdate();
  };

  const [runMutation, { loading: isSubmitting }] = useMutation(
    MODIFY_SEGMENT_PROPERTIES,
    {
      onCompleted: handleGetOfferResponse,
      onError: handleError,
      refetchQueries: [
        {
          query: GET_OFFER,
          variables: {
            params: {
              offerUuid,
              hydrateFinancialTerms: true,
            },
          },
        },
      ],
    },
  );

  const handleClick = (_: SyntheticEvent<HTMLElement, Event>) => cancelUpdate();

  const onSubmit = ({
    segmentUuid,
    endDate,
    endTime,
    startDate,
    startTime,
    updatedBy,
    segmentPriority,
  }: ModifySegmentFormData) => {
    const endTimeStamp = buildTimeStamp(format(endDate, DATE_FORMAT), endTime);
    const startTimeStamp = buildTimeStamp(
      format(startDate, DATE_FORMAT),
      startTime,
    );

    runMutation({
      variables: {
        params: {
          segmentUuid,
          segmentEndDate: cstToUTC(endTimeStamp),
          segmentStartDate: cstToUTC(startTimeStamp),
          updatedBy,
          segmentPriority: parseInt(segmentPriority),
        },
      },
    });
  };

  const segmentDisabled = isAfter(
    currentDate,
    parseISO(startDate) ?? currentDate,
  );

  return (
    <Container className={classes.container}>
      <Header as="h2">Modify Segment</Header>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <input type="hidden" value={uuid} name="segmentUuid" ref={register} />
          <Grid>
            <Grid.Row>
              <Grid.Column width={3}>
                <FormDateInput
                  name="startDate"
                  label="Start Date"
                  placeholder={new Date(startDate)}
                  disabled={segmentDisabled}
                />
              </Grid.Column>
              <Grid.Column width={3}>
                <FormTimeInput
                  name="startTime"
                  label="Start Time (CST)"
                  disabled={segmentDisabled}
                  placeholder={parseISO(startDate)}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={3}>
                <FormDateInput
                  name="endDate"
                  label="End Date"
                  placeholder={endDate ? new Date(endDate) : new Date()}
                />
              </Grid.Column>
              <Grid.Column width={3}>
                <FormTimeInput
                  name="endTime"
                  label="End Time (CST)"
                  useCST={endDate !== undefined}
                  placeholder={
                    endDate ? parseISO(endDate) : oneMinuteToMidnight()
                  }
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={3}>
                <FormInput
                  name="segmentPriority"
                  type="number"
                  step="any"
                  label="Segment Priority"
                  register={register({
                    required: true,
                    min: {
                      value: 0,
                      message:
                        'Segment Priority should be 0 or a positive number',
                    },
                  })}
                />
              </Grid.Column>
            </Grid.Row>
            <input name="updatedBy" type="hidden" ref={register} />
            <Grid.Row className={classes.formRow}>
              <button
                className={classnames(
                  classes.button,
                  classes.buttonSecondary,
                  classes.mediumBtn,
                )}
                onClick={handleClick}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || profileLoading || !formState.isValid}
                className={classnames(classes.button, classes.mediumBtn)}
              >
                {isSubmitting ? '...Submitting' : 'Save'}
              </button>
            </Grid.Row>
          </Grid>
        </form>
      </FormProvider>
    </Container>
  );
};

export default withStyles(styles)(ModifySegment);
